import { useState } from "react";
import { CalendarClock, Loader2, Pause, Play, Receipt, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Chip } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { money, shortDate } from "@/lib/format";

const STYLE = {
  active: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  paused: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  cancelled: "border-border bg-muted text-muted-foreground",
};

const FREQ_LABEL = { monthly: "every month", quarterly: "every quarter", annual: "once a year" };

export const RegularGiving = ({ mandates, currency, onChange }) => {
  const [busy, setBusy] = useState("");
  const [amounts, setAmounts] = useState({});

  const act = async (mandate, status) => {
    setBusy(`${mandate.id}-${status}`);
    try {
      const res = await api.post(`/payments/mandates/${mandate.id}/status`, { status });
      toast.success(res.data.note
        || (status === "cancelled" ? "Your regular gift is stopped"
          : status === "paused" ? "Paused — nothing will be collected"
            : "Restarted, thank you"));
      await onChange();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const saveAmount = async (mandate) => {
    const amount = Number(amounts[mandate.id]);
    if (!amount) return;
    setBusy(`${mandate.id}-amount`);
    try {
      const res = await api.post(`/payments/mandates/${mandate.id}/amount`, { amount });
      toast.success(res.data.note || `Your regular gift is now ${currency} ${amount}`);
      setAmounts({ ...amounts, [mandate.id]: "" });
      await onChange();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const annualReceipt = async (mandate) => {
    setBusy(`${mandate.id}-receipt`);
    try {
      const res = await api.post(`/payments/mandates/${mandate.id}/annual-receipt`);
      toast.success(`Receipt ${res.data.receipt_no} covers ${res.data.gift_count} gifts`);
      await onChange();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!mandates?.length) return null;

  return (
    <div className="space-y-3" data-testid="portal-mandates">
      {mandates.map((m) => (
        <div key={m.id} data-testid={`portal-mandate-${m.mandate_ref}`}
             className="rounded-xl border border-border bg-muted/20 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <CalendarClock className="h-4 w-4 text-primary" />
            <span className="text-[13px] font-medium text-foreground">
              {money(m.amount, m.currency || currency)} {FREQ_LABEL[m.frequency] || m.frequency}
            </span>
            <Chip className={STYLE[m.status] || STYLE.cancelled}>{m.status}</Chip>
            <span className="font-mono text-[10.5px] text-muted-foreground">{m.mandate_ref}</span>
            <span className="text-[11px] text-muted-foreground">
              {m.collected_count || 0} collected · to {m.fund_code}
              {m.next_run_on ? ` · next ${shortDate(m.next_run_on)}` : ""}
              {m.receipt_mode === "annual" ? " · one receipt a year" : " · receipt each time"}
            </span>
          </div>
          {m.status !== "cancelled" && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Input type="number" placeholder="New amount"
                     value={amounts[m.id] || ""} data-testid={`portal-mandate-amount-${m.mandate_ref}`}
                     onChange={(e) => setAmounts({ ...amounts, [m.id]: e.target.value })}
                     className="h-8 w-32 bg-muted/40 font-mono text-[12px]" />
              <Button size="sm" variant="outline" onClick={() => saveAmount(m)}
                      disabled={busy === `${m.id}-amount` || !amounts[m.id]}
                      data-testid={`portal-mandate-save-${m.mandate_ref}`}
                      className="h-8 rounded-full border-border text-[11px]">
                {busy === `${m.id}-amount` ? <Loader2 className="h-3 w-3 animate-spin" /> : "Change amount"}
              </Button>
              {m.status === "active" ? (
                <Button size="sm" variant="outline" onClick={() => act(m, "paused")}
                        disabled={busy === `${m.id}-paused`}
                        data-testid={`portal-mandate-pause-${m.mandate_ref}`}
                        className="h-8 rounded-full border-border text-[11px]">
                  <Pause className="mr-1 h-3 w-3" /> Pause
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => act(m, "active")}
                        disabled={busy === `${m.id}-active`}
                        data-testid={`portal-mandate-resume-${m.mandate_ref}`}
                        className="h-8 rounded-full border-border text-[11px]">
                  <Play className="mr-1 h-3 w-3" /> Restart
                </Button>
              )}
              {m.receipt_mode === "annual" && (
                <Button size="sm" variant="outline" onClick={() => annualReceipt(m)}
                        disabled={busy === `${m.id}-receipt`}
                        data-testid={`portal-mandate-receipt-${m.mandate_ref}`}
                        className="h-8 rounded-full border-border text-[11px]">
                  <Receipt className="mr-1 h-3 w-3" /> Annual receipt
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => act(m, "cancelled")}
                      disabled={busy === `${m.id}-cancelled`}
                      data-testid={`portal-mandate-cancel-${m.mandate_ref}`}
                      className="h-8 rounded-full text-[11px] text-muted-foreground">
                <X className="mr-1 h-3 w-3" /> Stop giving
              </Button>
            </div>
          )}
          {m.provider_action_required && (
            <p className="mt-2 text-[10.5px] text-amber-600 dark:text-amber-400"
               data-testid={`portal-mandate-note-${m.mandate_ref}`}>
              Recorded here — the charity is confirming the change with the payment provider.
            </p>
          )}
        </div>
      ))}
    </div>
  );
};
